import { UNAVAILABLE_DAYS } from "lib/constants";
import {
  DEFAULT_END_WORKING_HOUR,
  DEFAULT_START_WORKING_HOUR,
} from "lib/constants/date-constants";
import { getArrayOfKeyValuesFromArray } from "lib/utils/array-utils";
import {
  addMinutesToDateString,
  formatAsHour,
  formatAsMinute,
  getDateWithTime,
  getDateWithTimeFromTimePart,
  getHours,
  getHoursArrayFromTo,
  getItemsFromArrayComparingDate,
  getLocalISO,
  getUTCISO,
  getMinutes,
  isBefore,
  isLater,
  isSameOrBefore,
  isSameOrLater,
} from "lib/utils/date-utils";

const AppointmentBookingService = {
  getStartWorkingHours: (
    workingHours: WorkingHour[],
    date: Date
  ): AppointmentBookingTime => {
    if (!workingHours || !date) return DEFAULT_START_WORKING_HOUR;

    const day = date.getUTCDay();

    const selectedWorkingHour =
      day <= workingHours?.length ? workingHours[day] : null;

    if (!selectedWorkingHour) return DEFAULT_START_WORKING_HOUR;

    const [hour, minute] = selectedWorkingHour.fromTime.split(":");

    return {
      hour,
      minute,
    };
  },

  getEndWorkingHours: (
    workingHours: WorkingHour[],
    date: Date
  ): AppointmentBookingTime => {
    if (!workingHours || !date) return DEFAULT_END_WORKING_HOUR;

    const day = date.getUTCDay();

    const selectedWorkingHour =
      day <= workingHours?.length ? workingHours[day] : null;

    if (!selectedWorkingHour) return DEFAULT_END_WORKING_HOUR;

    const [hour, minute] = selectedWorkingHour.toTime.split(":");

    return {
      hour,
      minute,
    };
  },

  getAppointmentBooking: (
    workingHours: WorkingHour[],
    date: Date
  ): AppointmentBooking => {
    return {
      fromTime: AppointmentBookingService.getStartWorkingHours(
        workingHours,
        date
      ),
      toTime: AppointmentBookingService.getEndWorkingHours(workingHours, date),
    } as AppointmentBooking;
  },

  getDateFromAppointmentBooking: (
    date: Date,
    appointmentBookingTime: AppointmentBookingTime
  ): Date =>
    new Date(
      date?.getFullYear(),
      date?.getMonth(),
      date?.getDate(),
      Number(appointmentBookingTime?.hour),
      Number(appointmentBookingTime?.minute),
      0,
      0
    ),

  getAppointmentBookingFromDuration: (
    date: string,
    duration: number
  ): AppointmentBooking => {
    const startDate = new Date(date);
    const endDate = new Date(startDate.getTime() + duration * 60 * 1000);

    return {
      fromTime: {
        hour: startDate.getHours().toString(),
        minute: startDate.getMinutes().toString(),
      },
      toTime: {
        hour: endDate.getHours().toString(),
        minute: endDate.getMinutes().toString(),
      },
    };
  },

  markBookedSlots: (
    date: Date,
    bookedSlots: AppointmentBooking[],
    slotsToMark: AppointmentBooking[],
    sessionDuration: number
  ) => {
    for (let index = 0; index < slotsToMark.length; index++) {
      const slotToMark = slotsToMark[index];

      const slotFromMarkTime = getDateWithTime(date, slotToMark.fromTime);
      const slotToMarkTime = getDateWithTime(date, slotToMark.toTime);

      for (let bIndex = 0; bIndex < bookedSlots.length; bIndex++) {
        const bookedSlot = bookedSlots[bIndex];

        let bookedSlotStartTime = getDateWithTime(date, bookedSlot.fromTime);
        let bookedSlotToTime = getDateWithTime(date, bookedSlot.toTime);

        // Handle the case where the booked slot duration is greater than the session duration. Typically this would be the busy marked slot by practitioner or Admin.
        if (bookedSlot.duration > sessionDuration) {
          if (
            isSameOrLater(slotFromMarkTime, bookedSlotStartTime) &&
            isSameOrBefore(slotToMarkTime, bookedSlotToTime)
          ) {
            slotsToMark[index].isBooked = true;
            slotsToMark[index].appointmentId = bookedSlot.appointmentId;
            continue;
          }
        }

        bookedSlotStartTime = new Date(
          bookedSlotStartTime.setMinutes(bookedSlotStartTime.getMinutes() + 1)
        );
        bookedSlotToTime = new Date(
          bookedSlotToTime.setMinutes(bookedSlotToTime.getMinutes() - 1)
        );

        if (
          (bookedSlotStartTime.getTime() >= slotFromMarkTime.getTime() &&
            bookedSlotStartTime.getTime() <= slotToMarkTime.getTime()) ||
          (bookedSlotToTime.getTime() >= slotFromMarkTime.getTime() &&
            bookedSlotToTime.getTime() <= slotToMarkTime.getTime()) ||
          (bookedSlotStartTime.getTime() <= slotFromMarkTime.getTime() &&
            bookedSlotToTime.getTime() >= slotToMarkTime.getTime())
        ) {
          slotsToMark[index].isBooked = true;
          slotsToMark[index].appointmentId = bookedSlots[bIndex]?.appointmentId;
        }
      }
    }

    return slotsToMark;
  },

  getAvailableBookingSlots: (
    date: Date,
    startTime: Date,
    endTime: Date,
    sessionDuration: number,
    bookedSlots: AppointmentBookingDetails[]
  ): AppointmentBooking[] => {
    const output: AppointmentBooking[] = [];

    if (!date || !startTime || !endTime || !sessionDuration) return output;

    const segments = getHoursArrayFromTo(
      getHours(startTime.toString()),
      getHours(endTime.toString()),
      sessionDuration
    );

    for (let i = 0; i < segments.length - 1; i++) {
      const fromTime: AppointmentBookingTime = {
        hour: segments[i].hour,
        minute: segments[i].minute,
      };

      const toTime: AppointmentBookingTime = {
        hour: segments[i + 1].hour,
        minute: segments[i + 1].minute,
      };

      output.push({ fromTime, toTime });
    }

    const filteredBookedSlots = getArrayOfKeyValuesFromArray(
      getItemsFromArrayComparingDate(
        bookedSlots ?? [],
        "fromTime",
        date.toString()
      ),
      ["fromTime", "duration", "id"]
    )?.map((item) => ({
      ...AppointmentBookingService.getAppointmentBookingFromDuration(
        item["fromTime"],
        item["duration"]
      ),
      appointmentId: item["id"],
      isBooked: true,
    }));

    AppointmentBookingService.markBookedSlots(
      date,
      filteredBookedSlots,
      output,
      sessionDuration
    );

    return output;
  },

  getAvailableBookingSlotsWithBookedSlots: (
    date: Date,
    startTime: Date,
    endTime: Date,
    sessionDuration: number,
    bookedSlots: AppointmentBooking[]
  ): AppointmentBooking[] => {
    const output: AppointmentBooking[] = [];

    if (!date || !startTime || !endTime || !sessionDuration) return output;

    const segments = getHoursArrayFromTo(
      getHours(startTime.toString()),
      getHours(endTime.toString()),
      sessionDuration
    );

    for (let i = 0; i < segments.length - 1; i++) {
      const fromTime: AppointmentBookingTime = {
        hour: segments[i].hour,
        minute: segments[i].minute,
      };

      const toTime: AppointmentBookingTime = {
        hour: segments[i + 1].hour,
        minute: segments[i + 1].minute,
      };

      output.push({ fromTime, toTime });
    }

    AppointmentBookingService.markBookedSlots(
      date,
      bookedSlots,
      output,
      sessionDuration
    );

    return output;
  },

  appointmentBookingTimeToString: (booking: AppointmentBookingTime) =>
    `${formatAsHour(Number(booking.hour))}:${formatAsMinute(
      Number(booking.minute)
    )}`,

  formatBookingSlots: (bookingSlots: AppointmentBooking[]): string[] => {
    const output = (bookingSlots ?? [])
      .sort((a, b) => {
        const fromA = getDateWithTime(new Date(), a.fromTime);
        const fromB = getDateWithTime(new Date(), b.fromTime);
        return fromA.getTime() - fromB.getTime();
      })
      .map((slot) => {
        const fromTime = `${formatAsHour(
          Number(slot.fromTime.hour)
        )}:${formatAsMinute(Number(slot.fromTime.minute))}`;
        const toTime = `${formatAsHour(
          Number(slot.toTime.hour)
        )}:${formatAsMinute(Number(slot.toTime.minute))}`;

        return `${fromTime} - ${toTime}`;
      });

    return output;
  },

  formatBookingDetail: (
    bookingDetail: AppointmentBookingDetails
  ): AppointmentBooking => {
    const fromHours = getHours(bookingDetail.fromTime);
    const fromMinutes = getMinutes(bookingDetail.fromTime);

    const toDate = addMinutesToDateString(
      bookingDetail.fromTime,
      bookingDetail.duration
    );

    const toHours = getHours(getUTCISO(toDate));
    const toMinutes = getMinutes(getUTCISO(toDate));

    return {
      fromTime: {
        hour: formatAsHour(fromHours),
        minute: formatAsMinute(fromMinutes),
      },
      toTime: {
        hour: formatAsHour(toHours),
        minute: formatAsMinute(toMinutes),
      },
      isBooked: true,
      appointmentId: bookingDetail.id,
    };
  },

  formatBookingDetails: (
    bookingDetails: AppointmentBookingDetails[]
  ): AppointmentBooking[] =>
    (bookingDetails ?? [])
      .sort(
        (a, b) =>
          new Date(a.fromTime).getHours() - new Date(b.fromTime).getHours()
      )
      .map((bookingDetail) =>
        AppointmentBookingService.formatBookingDetail(bookingDetail)
      ),

  formatBookingAsUnavailableDate: (
    bookingDetail: AppointmentBookingDetails
  ): UnavailableDay => ({
    date: new Date(bookingDetail.fromTime).toDateString(),
    internalDate: new Date(bookingDetail.fromTime),
    title: "Appointments",
    type: UNAVAILABLE_DAYS.HOLIDAY,
  }),

  formatBookingsAsUnavailableDates: (
    bookingDetails: AppointmentBookingDetails[]
  ): UnavailableDay[] =>
    (bookingDetails ?? []).map((bookingDetail) =>
      AppointmentBookingService.formatBookingAsUnavailableDate(bookingDetail)
    ),

  isBookingOverlapping: (
    bookings: AppointmentBooking[],
    workingTime: WorkingTime
  ): boolean => {
    if (bookings?.length === 0 || !workingTime) return false;

    const fromDate = getDateWithTimeFromTimePart(
      new Date(),
      workingTime.fromTime
    );
    const toDate = getDateWithTimeFromTimePart(new Date(), workingTime.toTime);

    let isBetween = bookings.some((booking) => {
      const bookingFromDate = getDateWithTime(new Date(), booking.fromTime);
      const bookingToDate = getDateWithTime(new Date(), booking.toTime);

      return (
        isSameOrLater(fromDate, bookingFromDate) &&
        isSameOrBefore(fromDate, bookingToDate)
      );
    });

    if (isBetween) return true;

    isBetween = bookings.some((booking) => {
      const bookingFromDate = getDateWithTime(new Date(), booking.fromTime);
      const bookingToDate = getDateWithTime(new Date(), booking.toTime);

      return (
        isLater(toDate, bookingFromDate) && isBefore(toDate, bookingToDate)
      );
    });

    if (isBetween) return true;

    isBetween = bookings.some((booking) => {
      const bookingFromDate = getDateWithTime(new Date(), booking.fromTime);
      const bookingToDate = getDateWithTime(new Date(), booking.toTime);

      return (
        isSameOrBefore(fromDate, bookingFromDate) &&
        isSameOrLater(toDate, bookingToDate)
      );
    });

    return isBetween;
  },

  formatISODatesAsUnavailableDays: (isoDates: string[]): UnavailableDay[] => {
    const uniqueDates = new Set(isoDates);

    return (Array.from(uniqueDates) ?? []).map((isoDate) => ({
      date: isoDate,
      internalDate: new Date(isoDate),
      title: "Unavailable",
      type: UNAVAILABLE_DAYS.UNAVAILABLE,
    }));
  },

  formatDateAsAppointmentBookingTime: (
    date: string | Date
  ): AppointmentBookingTime => {
    if (!date) return { hour: "00", minute: "00" };

    const dateTime = new Date(date);
    return {
      hour: formatAsHour(dateTime.getHours()),
      minute: formatAsMinute(dateTime.getMinutes()),
    };
  },

  formatDateAsAppointmentBookingTimeAndDuration: (
    date: string | Date,
    duration: number
  ): AppointmentBookingTime => {
    if (!date) return { hour: "00", minute: "00" };

    const dateTime = new Date(new Date(date).getTime() + duration * 60 * 1000);
    return {
      hour: formatAsHour(dateTime.getHours()),
      minute: formatAsMinute(dateTime.getMinutes()),
    };
  },

  getDurationFromBooking: (
    fromTime: AppointmentBookingTime,
    toTime: AppointmentBookingTime
  ): number => {
    if (!fromTime || !toTime) return 0;

    const fromDate = getDateWithTime(new Date(), fromTime);
    const toDate = getDateWithTime(new Date(), toTime);

    return (toDate.getTime() - fromDate.getTime()) / (1000 * 60); // Convert milliseconds to minutes
  },

  getClinicWorkingHours: (workingHours: WorkingHour[]): string =>
    (workingHours ?? [])
      .map(
        (workingHour) =>
          `${workingHour.day}: ${workingHour.fromTime} - ${workingHour.toTime}`
      )
      .join(", "),
};

export default AppointmentBookingService;
